import {Component, OnDestroy, OnInit} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {AbstractControl, FormGroup, UntypedFormArray, UntypedFormBuilder} from '@angular/forms';
import {NavigationService} from '../../../../service/navigation.service';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {InventoryMappingService} from '../../../../service/inventory-mapping.service';
import {InventoryMappingRequestModel} from '../../../../model/inventory-mapping-request.model';
import {AccountInventoryField} from '../../../../model/account-inventory-field';
import {AccountService} from '../../../../service/account.service';
import {InventoryFieldModel} from '../../../../model/inventory-field.model';
import {forkJoin, Subscription} from 'rxjs';

@Component({
    selector: 'auto-inventory-mapping',
    templateUrl: './inventory-mapping.component.html',
    styleUrl: './inventory-mapping.component.scss',
    standalone: false
})
export class InventoryMappingComponent implements OnInit, OnDestroy {

  accountId: number;
  settingsSideNav: MenuItem[];
  inventoryMappingForm: FormGroup;
  offerFieldOptions: Array<{label: string; value: string; hidden: boolean}> = [];
  inventoryHeaders: string[] = [];
  inventoryFields: InventoryFieldModel[] = [];
  separators = ['|', ',', ' '];
  inventoryMappingFields: AccountInventoryField[] = [];
  isFieldLoaded = false;
  duplicateFields: Set<number> = new Set();
  inventoryProviderId: number;
  requestProcessing = false;
  private routerSubscription: Subscription;
  private routeSubscription?: Subscription;
  private readonly inventoryFeeFieldKeys = ['upfit_1', 'upfit_2', 'dealer_adjustment', 'fee_1', 'fee_2'];

  constructor(private navigationService: NavigationService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: UntypedFormBuilder,
              private inventoryMappingService: InventoryMappingService,
              private messageService: MessageService,
              private accountService: AccountService,
              private confirmationService: ConfirmationService
  )
  {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart
        && this.inventoryMappingForm?.dirty
        && !confirm('You have unsaved changes. Do you really want to leave?')
      ) {
        this.router.navigateByUrl(this.router.url);
      }
    });
  }

  ngOnInit(): void
  {
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      this.accountId = parseInt(params.get('accountId'));
      this.initNavigation();
      this.getInventoryFields();
    });
  }

  ngOnDestroy(): void
  {
    this.routeSubscription?.unsubscribe();
    this.routerSubscription.unsubscribe();
  }

  /**
   * Get inventory mapping fields
   */
  getInventoryMappingFields(accountId: number): void
  {
    this.setRequestProcessing(true);
    forkJoin({
      mappings: this.inventoryMappingService.getInventoryMappingFields(accountId),
      account: this.accountService.getAccount(accountId)
    }).subscribe({
      next: ({mappings, account}) => {
        this.inventoryMappingFields = mappings;
        this.inventoryProviderId = account.inventoryProviderId;
        this.initForm();
        this.isFieldLoaded = true;
        this.setRequestProcessing(false);
      },
      error: () => this.handleMappingRequestFailure('Failed to load inventory mapping.')
    });
  }

  /**
   * Get fields
   */
  get fields(): UntypedFormArray {
    return this.inventoryMappingForm.get('fields') as UntypedFormArray;
  }

  /**
   * Initialize form group array
   */
  private initForm() {
    this.inventoryMappingForm = this.formBuilder.group({
      fields: this.formBuilder.array([]),
    });
    this.initializeMappingFields();
  }

  /**
   * Init navigation
   */
  initNavigation() {
    this.navigationService.getMenuItems(this.accountId, 'inventory-settings-view-side-menu.configuration.json')
      .then((navItems) => {
        this.settingsSideNav = navItems;
      });
  }

  /**
   * Get inventory fields
   */
  private getInventoryFields(): void
  {
    this.setRequestProcessing(true);
    this.inventoryMappingService.getInventoryFields()
      .subscribe({
          next: (inventoryFields) => {
            this.setInventoryFields(inventoryFields);
            this.getInventoryHeaders();
          },
          error: () => this.handleMappingRequestFailure('Failed to load inventory fields.')
        }
      );
  }

  /**
   * Update inventory mapping
   */
  updateInventoryMapping(): void
  {
    if (!this.requestProcessing) {
      try {
        const request = this.buildInventoryMappingRequestModel();
        const previousSourceKeys = this.getEffectiveInventoryFeeKeys(this.inventoryMappingFields);
        this.setRequestProcessing(true);
        this.inventoryMappingService.updateInventoryMapping(request).subscribe({
          next: () => this.refreshSavedMapping(previousSourceKeys, 'Inventory mapping updated successfully.'),
          error: () => this.handleMappingRequestFailure('Failed to update inventory mapping.')
        });
      } catch (error: unknown) {
        console.error('Unable to prepare inventory mapping changes.', {accountId: this.accountId, error});
        this.handleMappingRequestFailure('Select an offer field for each inventory mapping before saving.');
      }
    }
  }

  /**
   * Build inventory mapping request model
   */
  private buildInventoryMappingRequestModel(): InventoryMappingRequestModel
  {
    const inventoryMappingRequestModel = new InventoryMappingRequestModel();
    inventoryMappingRequestModel.accountId = this.accountId;
    inventoryMappingRequestModel.inventoryMappingFields = this.fields.controls.map(field => this.buildAccountInventoryField(field));
    const retainedIds = new Set(inventoryMappingRequestModel.inventoryMappingFields.map(field => field.id));
    inventoryMappingRequestModel.deletedInventoryMappingFields = this.inventoryMappingFields
      .filter(field => !retainedIds.has(field.id))
      .map(field => field.id);

    return inventoryMappingRequestModel;
  }

  /** Resolves source identity from the catalog, including new rows without a saved ID. */
  private buildAccountInventoryField(field: AbstractControl): AccountInventoryField
  {
    const name: string = field.get('name').value;
    const inventoryField = this.inventoryFields.find(candidate => candidate.name === name);
    if (!inventoryField) {
      throw new Error('The selected inventory field is unavailable.');
    }
    const accountInventoryField = new AccountInventoryField();
    accountInventoryField.id = field.get('id')?.value ?? null;
    accountInventoryField.name = name;
    accountInventoryField.colNum = this.getInventoryColumn(field, inventoryField);
    accountInventoryField.isMultiValue = field.get('isMultiValue').value;
    accountInventoryField.multiValueSeparator = field.get('multiValueSeparator').value;
    accountInventoryField.inventoryFieldId = inventoryField.id;
    accountInventoryField.inventoryProviderId = this.inventoryProviderId;
    accountInventoryField.niceName = inventoryField.niceName;
    accountInventoryField.ncsAccountId = this.accountId;

    return accountInventoryField;
  }

  /** Preserves only unchanged unmapped source placeholders created by the legacy account editor. */
  private getInventoryColumn(field: AbstractControl, inventoryField: InventoryFieldModel): string
  {
    const header: string | null | undefined = field.get('inventoryHeader').value;
    const columnIndex = this.inventoryHeaders.indexOf(header);
    let column = columnIndex.toString();
    const savedMapping = this.inventoryMappingFields.find(mapping => mapping.id === field.get('id')?.value);
    const hasUnsetHeader = header === null || header === undefined || header === '';
    if (columnIndex < 0 && hasUnsetHeader && savedMapping?.colNum === ''
      && savedMapping.inventoryFieldId === inventoryField.id
      && savedMapping.niceName === inventoryField.niceName
      && this.inventoryFeeFieldKeys.includes(inventoryField.niceName)) {
      column = '';
    }

    return column;
  }

  /**
   * Open reset to default confirmation
   */
  openResetToDefaultConfirmation() {
    this.confirmationService.confirm({
      message: 'Do you want to reset Inventory Mapping to default?',
      header: 'Reset to Default',
      acceptLabel: 'Reset',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Reset',
        severity: 'success'
      },
      accept: () => {
        this.resetInventoryMapping();
      },
      reject: () => {}
    });
  }

  /**
   * Reset inventory mapping
   */
  resetInventoryMapping(): void
  {
    if (!this.requestProcessing) {
      const previousSourceKeys = this.getEffectiveInventoryFeeKeys(this.inventoryMappingFields);
      this.setRequestProcessing(true);
      this.inventoryMappingService.resetInventoryMapping(this.accountId).subscribe({
        next: () => this.refreshSavedMapping(previousSourceKeys, 'Inventory mapping reset successfully.'),
        error: () => this.handleMappingRequestFailure('Failed to reset inventory mapping.')
      });
    }
  }

  /** Requires both persisted mappings and current source visibility before reporting fee changes. */
  private refreshSavedMapping(previousSourceKeys: string[], successMessage: string): void
  {
    forkJoin({
      catalog: this.inventoryMappingService.getInventoryFields(),
      mappings: this.inventoryMappingService.getInventoryMappingFields(this.accountId)
    }).subscribe({
      next: ({catalog, mappings}) => {
        this.setInventoryFields(catalog);
        this.inventoryMappingFields = mappings;
        this.initForm();
        this.hasDuplicateField();
        this.setRequestProcessing(false);
        this.messageService.add({severity: 'success', summary: 'Success', detail: successMessage});
        this.notifyInventoryFeeMappingChanges(previousSourceKeys);
      },
      error: () => this.handleMappingRequestFailure(
        'Inventory mapping was saved, but could not be reloaded. Reload this page to review the saved mappings.'
      )
    });
  }

  /** Replaces catalog choices so a refreshed flag state cannot leave stale source options. */
  private setInventoryFields(inventoryFields: InventoryFieldModel[]): void
  {
    this.inventoryFields = inventoryFields;
    this.offerFieldOptions = inventoryFields.map(field => ({
      label: field.name,
      value: field.name,
      hidden: this.isRequiredField(field.name)
    }));
    this.offerFieldOptions.sort((first, second) => first.label.localeCompare(second.label));
  }

  /** Empty, unset and invalid columns do not make a source available to the fee resolver. */
  private getEffectiveInventoryFeeKeys(mappings: AccountInventoryField[]): string[]
  {
    const mappedSourceKeys = mappings
      .filter(field => this.inventoryFeeFieldKeys.includes(field.niceName) && /^\d+$/.test(field.colNum ?? ''))
      .map(field => field.niceName);

    return [...new Set(mappedSourceKeys)];
  }

  /** A hidden source catalog cannot be interpreted as a successful removal of saved mappings. */
  private notifyInventoryFeeMappingChanges(previousSourceKeys: string[]): void
  {
    const visibleSourceKeys = this.inventoryFields.map(field => field.niceName);
    const currentSourceKeys = this.getEffectiveInventoryFeeKeys(this.inventoryMappingFields);
    const addedSources = currentSourceKeys.filter(key => visibleSourceKeys.includes(key) && !previousSourceKeys.includes(key));
    const removedSources = previousSourceKeys.filter(key => visibleSourceKeys.includes(key) && !currentSourceKeys.includes(key));
    if (addedSources.length > 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Inventory fee mapping added',
        detail: 'Configure or review the mapped fees in the New and Used Fees sections. '
          + 'Any retained fee configuration resumes on the next normal recalculation.'
      });
    }
    if (removedSources.length > 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Inventory fee mapping removed',
        detail: 'Affected fees stop applying on the next normal recalculation. '
          + 'Use Save & Sync in the affected Fees section to update offers sooner.'
      });
    }
  }

  /** Locks controls through the reread so it cannot overwrite edits made during a save. */
  private setRequestProcessing(processing: boolean): void
  {
    this.requestProcessing = processing;
    if (this.inventoryMappingForm) {
      if (processing) {
        this.inventoryMappingForm.disable({emitEvent: false});
      }
      if (!processing) {
        this.inventoryMappingForm.enable({emitEvent: false});
        this.fields.controls.forEach(field => {
          if (this.isRequiredField(field.get('name').value)) {
            field.get('name').disable({emitEvent: false});
          }
        });
      }
    }
  }

  /** Leaves the current form available for correction after a controlled request failure. */
  private handleMappingRequestFailure(detail: string): void
  {
    this.setRequestProcessing(false);
    this.messageService.add({severity: 'error', summary: 'Error', detail});
  }

  /**
   * Get inventory headers
   */
  private getInventoryHeaders(): void
  {
    this.inventoryMappingService.getInventoryHeaders(this.accountId).subscribe(
      {
        next: (inventoryHeaders) => {
          this.inventoryHeaders = inventoryHeaders;
          this.getInventoryMappingFields(this.accountId);
        },
        error: () => {
          this.setRequestProcessing(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            life: 10000000, // life is set to a large number to ensure a user knows that an inventory file is required
            detail: 'No inventory file for account found.'
          });
        }
      }
    );
  }

  /**
   * Check if field is required
   */
  isRequiredField(name) {
    return !!this.inventoryFields.find((field) => field.name === name && field.required);
  }

  /**
   * Check if field is duplicate
   */
  hasDuplicateField() {
    this.duplicateFields.clear();
    const fieldValues = this.fields.controls.map((field, index) => ({
      value: field.get('name')?.value,
      index: index
    }));

    fieldValues.forEach((field, index) => {
      if (field.value && fieldValues.filter(f => f.value === field.value).length > 1) {
        this.duplicateFields.add(index);
      }
    });
  }

  /**
   * Initialize mapping fields
   */
  private initializeMappingFields() {
    this.inventoryMappingFields = this.inventoryMappingFields.sort((first, second) => {
      return (+this.isRequiredField(second.name)) - (+this.isRequiredField(first.name));
    });
    this.inventoryMappingFields.forEach(
      (field) => {
        this.fields.push(this.formBuilder.group({
          id: [field.id],
          name: [{value: field.name, disabled: this.isRequiredField(field.name)}],
          inventoryHeader: [this.inventoryHeaders[field.colNum]],
          inventoryFieldId: [field.inventoryFieldId],
          isMultiValue: [field.isMultiValue],
          niceName: [field.niceName],
          multiValueSeparator: [field.multiValueSeparator],
          inventoryProviderId: [field.inventoryProviderId]
        }));
      }
    );
  }

  /**
   * Get filtered offer field options
   */
  getFilteredOfferFieldOptions(selectedValue: string) {
    return this.offerFieldOptions.filter((option) => !option.hidden || option.value === selectedValue);
  }

  /**
   * Add inventory mapping
   */
  addInventoryMapping(): void
  {
    this.fields.push(this.formBuilder.group({
      id: [null],
      name: [''],
      inventoryHeader: [''],
      isMultiValue: [false],
      multiValueSeparator: ['']
    }));
  }

  /**
   * Delete mapping field
   */
  deleteMappingField(index: number) {
    if (this.fields.length > index) {
      this.fields.removeAt(index);
      this.inventoryMappingForm.markAsDirty();
    }
    this.hasDuplicateField();
  }
}
